/**
 * ════════════════════════════════════════════════════════════════════════════
 * SOLID BACKGROUNDS ON SEALS AND SIGNATURES (T-125, T-126)
 *
 * A seal or signature is drawn over the certificate. If its image has a solid white
 * background, that white is drawn too — as a rectangle that hides the form's printed text,
 * the dotted signature line, and any mark beneath it. BCQ's ACCI seal (91% solid white)
 * and the K Samios signature (87%) both did exactly that, and upload only warned about
 * JPEGs, so a PNG without transparency got through without a word.
 *
 * Two functions, one used on each side:
 *
 *   analyseImageBackground — the server, on upload and in the libraries, to say so.
 *   clearLightBackground   — the browser, to offer a transparent version with a preview.
 *
 * Both work on raw RGBA pixels so neither needs an image library here; the caller decodes.
 * ════════════════════════════════════════════════════════════════════════════
 */
/**
 * The share above which an image counts as having a solid background.
 *
 * Low on purpose. A transparent seal scores ~0; a logo scanned on paper scores well above
 * half. A seal with a small white plate behind its crest is flagged too — which is right,
 * because that plate will still hide whatever it is placed over.
 */
const SOLID_THRESHOLD = 0.15;
/** How light a pixel must be to count as background: every channel at or above this. */
const LIGHT = 235;
export function analyseImageBackground(rgba, width, height) {
    const pixels = width * height;
    if (pixels === 0)
        return { solid: false, opaqueLightShare: 0 };
    let light = 0;
    for (let i = 0; i < pixels * 4; i += 4) {
        if (rgba[i + 3] >= 250 && rgba[i] >= LIGHT && rgba[i + 1] >= LIGHT && rgba[i + 2] >= LIGHT)
            light += 1;
    }
    const opaqueLightShare = light / pixels;
    return { solid: opaqueLightShare > SOLID_THRESHOLD, opaqueLightShare };
}
/**
 * Makes a light background transparent, in place, keeping the ink's colour.
 *
 * "Colour to alpha" against white rather than a threshold cut-out: each pixel keeps
 * exactly as much opacity as it differs from white, and its colour is un-blended so that
 * over white paper it looks as it did. A threshold would leave a jagged white fringe
 * around every letter of "Issued at Brisbane, QLD"; this leaves anti-aliased edges and
 * red that stays red. Faint paper noise (under 4%) is cleared entirely.
 */
export function clearLightBackground(rgba) {
    for (let i = 0; i < rgba.length; i += 4) {
        const r = rgba[i] / 255;
        const g = rgba[i + 1] / 255;
        const b = rgba[i + 2] / 255;
        const original = rgba[i + 3] / 255;
        let alpha = 1 - Math.min(r, g, b);
        alpha = Math.min(1, Math.max(0, (alpha - 0.04) / 0.96));
        if (alpha === 0) {
            rgba[i + 3] = 0;
            continue;
        }
        const unblend = (c) => Math.round(Math.min(1, Math.max(0, (c - (1 - alpha)) / alpha)) * 255);
        rgba[i] = unblend(r);
        rgba[i + 1] = unblend(g);
        rgba[i + 2] = unblend(b);
        rgba[i + 3] = Math.round(alpha * original * 255);
    }
}
//# sourceMappingURL=image-background.js.map